import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, normalizeClass as _normalizeClass, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["aria-label"]
const _hoisted_2 = {
  key: 0,
  class: "tw-absolute tw-inset-x-0 tw-top-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-slate-200 tw-to-transparent"
}
const _hoisted_3 = {
  key: 1,
  class: "tw-absolute tw-inset-x-0 tw-top-0 tw-h-12 tw-bg-gradient-to-b tw-from-slate-50 tw-to-transparent"
}
const _hoisted_4 = {
  key: 2,
  class: "tw-relative tw-flex tw-items-start tw-justify-between tw-gap-4 tw-border-b tw-border-slate-200/70 tw-px-5 tw-py-4"
}
const _hoisted_5 = { class: "tw-min-w-0" }
const _hoisted_6 = {
  key: 0,
  class: "tw-text-xl tw-font-semibold tw-text-slate-900"
}
const _hoisted_7 = {
  key: 1,
  class: "tw-text-xl tw-font-semibold tw-text-slate-900"
}
const _hoisted_8 = {
  key: 2,
  class: "tw-text-base tw-text-slate-600 tw-mt-0.5"
}
const _hoisted_9 = {
  key: 3,
  class: "tw-text-base tw-text-slate-600 tw-mt-0.5"
}

import { Teleport, Transition, computed, onBeforeUnmount, useSlots, watch } from 'vue';
import { useModalStack } from './useModalStack';


export default /*@__PURE__*/_defineComponent({
  __name: 'Modal',
  props: /*@__PURE__*/_mergeModels({
    modalId: { default: () => `modal-${Date.now()}-${Math.random().toString(36).slice(2, 11)}` },
    title: {},
    subtitle: {},
    ariaLabel: {},
    hasCloseButton: { type: Boolean, default: true },
    isClosableOnBackdrop: { type: Boolean, default: true },
    isClosableOnEsc: { type: Boolean, default: true },
    maxWidth: { default: '6xl' }
  }, {
    "isOpen": { type: Boolean, ...{ default: false } },
    "isOpenModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["close"], ["update:isOpen"]),
  setup(__props: any, { expose: __expose, emit: __emit }) {

const isOpen = _useModel<boolean>(__props, 'isOpen');

const props = __props;

const emit = __emit;

const slots = useSlots();
const { pushModal, removeModal, isTopModal } = useModalStack();

const hasHeader = computed(() => {
  return (
    !!props.hasCloseButton ||
    !!props.title ||
    !!props.subtitle ||
    !!slots.title ||
    !!slots.subtitle
  );
});

const maxWidthClass = computed(() => {
  const map: Record<string, string> = {
    sm: 'tw-max-w-sm',
    md: 'tw-max-w-md',
    lg: 'tw-max-w-lg', //32rem
    xl: 'tw-max-w-xl', // 36rem (+4rem)
    '2xl': 'tw-max-w-2xl', // 42rem (+6rem)
    '3xl': 'tw-max-w-3xl', // 48rem (+6rem)
    '4xl': 'tw-max-w-4xl', // 56rem (+8rem)
    '5xl': 'tw-max-w-5xl', // 64rem (+8rem)
    '6xl': 'tw-max-w-[70vw]', // 72rem (+8rem)
    '7xl': 'tw-max-w-[80vw]', // 80rem
  };
  return map[props.maxWidth] || 'tw-max-w-6xl';
});

/** User-initiated close (X button, backdrop, Esc, slot close()) - only top modal reacts; emits @close */
function requestClose() {
  if (!isOpen.value) return;
  if (!isTopModal(props.modalId)) return;
  isOpen.value = false;
  emit('close');
}

function onBackdropClick() {
  if (!props.isClosableOnBackdrop) return;
  requestClose();
}

let isStackRegistered = false;

watch(
  isOpen,
  (open) => {
    if (open && !isStackRegistered) {
      pushModal({
        id: props.modalId,
        isOpen: true,
        close: requestClose,
        isClosableOnEsc: () => props.isClosableOnEsc,
      });
      isStackRegistered = true;
    } else if (!open && isStackRegistered) {
      removeModal(props.modalId);
      isStackRegistered = false;
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  if (isStackRegistered) {
    removeModal(props.modalId);
    isStackRegistered = false;
  }
});

// Imperative escape hatch: lets a parent close the modal via a template ref
// (`<Modal ref="modalRef" />` then `modalRef.value.close()`). The normal way
// to close is to flip `v-model:isOpen` to false; only reach for this when the
// parent doesn't own the open state but still needs to force a close.
__expose({
  close: requestClose,
});

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    (isOpen.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "tw-fixed tw-inset-0 tw-z-[3000] tw-flex tw-items-end sm:tw-items-center tw-justify-center tw-p-4",
          role: "dialog",
          "aria-modal": "true",
          "aria-label": __props.ariaLabel || 'Modal'
        }, [
          _createElementVNode("div", {
            class: "tw-absolute tw-inset-0 tw-bg-slate-900/40 tw-backdrop-blur-sm",
            onClick: onBackdropClick
          }),
          (isOpen.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: _normalizeClass(["tw-relative tw-flex tw-w-full tw-max-h-[85vh] tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-bg-white tw-shadow-2xl tw-shadow-slate-900/20 tw-ring-1 tw-ring-slate-200", maxWidthClass.value])
              }, [
                (hasHeader.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_2))
                  : _createCommentVNode("", true),
                (hasHeader.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_3))
                  : _createCommentVNode("", true),
                (hasHeader.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                      _createElementVNode("div", _hoisted_5, [
                        (_ctx.$slots.title)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                              _renderSlot(_ctx.$slots, "title")
                            ]))
                          : (__props.title)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(__props.title), 1))
                            : _createCommentVNode("", true),
                        (_ctx.$slots.subtitle)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                              _renderSlot(_ctx.$slots, "subtitle")
                            ]))
                          : (__props.subtitle)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_9, _toDisplayString(__props.subtitle), 1))
                            : _createCommentVNode("", true)
                      ]),
                      (__props.hasCloseButton)
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 0,
                            type: "button",
                            class: "tw-inline-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white/70 tw-text-slate-700 hover:tw-bg-slate-100 hover:tw-border-slate-300 hover:tw-text-slate-900",
                            "aria-label": "Close",
                            onClick: requestClose
                          }, [
                            _createVNode(_component_font_awesome_icon, {
                              icon: "xmark",
                              class: "tw-h-4 tw-w-4"
                            })
                          ]))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", {
                  class: _normalizeClass(["tw-relative tw-overflow-auto tw-px-5 tw-py-4", hasHeader.value ? 'tw-max-h-[calc(85vh-64px)]' : 'tw-max-h-[85vh]'])
                }, [
                  (_unref(slots).content)
                    ? _renderSlot(_ctx.$slots, "content", {
                        key: 0,
                        close: requestClose
                      })
                    : _renderSlot(_ctx.$slots, "default", { key: 1 })
                ], 2)
              ], 2))
            : _createCommentVNode("", true)
        ], 8, _hoisted_1))
      : _createCommentVNode("", true)
  ]))
}
}

})